import React, { PropsWithChildren, ReactNode } from 'react';
import theme from './NotificationRow.module.scss';
import { classnames } from '@/utils/html';
import { DateDistanceDisplay } from '@/components/localization';
import { CustomLink } from '@/components/navigation';
import BackendIcon, { BackendIconNames } from '@/components/Icons/BackendIcon';
import { ProfileIcon } from '@/components/Icons';
import { Trans } from '@lingui/react/macro';
import DeprecatedContent from './DeprecatedNotificationContent';
import {
    ChangeRequestAcceptedData,
    ChangeRequestClosedData,
    OfferRejectedByBuyerData,
    ChangeRequestAddedParticipantData,
    DocumentAddedData,
    ChangeRequestAddedAttachmentData,
    TaskChangeData,
    NewLandingPageMessageData,
    ProjectInviteAcceptedData,
    InvitedUserAcceptedData,
    TaggedInChatData,
    DeadlineApproachingData,
    OfferOnChangeRequestToBuyerData,
    ChangeRequestCreatedData,
    TaskType,
    DeadlineReminderAllSentData,
    DeadlineReminderNoOptionsData,
    DeadlineReminderWithUnsentData,
    BuyerSignedOptionsData,
    SendChangeRequestRequestedNewOffersData,
    TaggedInChatVer2,
    INotification,
    NotificationTemplateType,
    SendZippedFileReadyData,
} from '@/types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArchive } from '@fortawesome/pro-light-svg-icons';
import ExternalLink from '@/components/navigation/ExternalLink';
import { getZipDownloadUrl } from '@/services/documents';
import { useLingui } from '@lingui/react';
type DataProps = PropsWithChildren<{
    notification: INotification;
    handleClick: Function;
}>;

type NoLinkRowProps = DataProps & {
    icon: ReactNode;
};

type LinkRowProps = NoLinkRowProps & {
    url: string;
};

interface NotificationRowProps {
    notification: INotification;
    onClick?: (notification: INotification) => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({ notification, onClick }) => {
    const handleClick = (e) => {
        if (onClick) {
            onClick(notification);
        }
    };

    switch (notification.templateType) {
        case NotificationTemplateType.OldNotification:
            return <DeprecatedContent notification={notification} onClick={onClick} />;
        case NotificationTemplateType.ChangeRequestCreated:
            return <ChangeRequestCreated notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.OfferOnChangeRequestToBuyer:
            return <OfferOnChangeRequestToBuyer notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.DeadlineApproaching:
            return <DeadlineApproaching notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.TaggedInChat:
            return <TaggedInChat notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.InvitedUserAccepted:
            return <InvitedUserAccepted notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.ProjectInviteAccepted:
            return <ProjectInviteAccepted notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.NewLandingPageMessage:
            return <NewLandingPageMessage notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.TaskChange:
            return <TaskChange notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.ChangeRequestAddedAttachment:
            return <ChangeRequestAddedAttachment notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.DocumentAdded:
            return <DocumentAdded notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.ChangeRequestAddedParticipant:
            return <ChangeRequestAddedParticipant notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.OfferRejectedByBuyer:
            return <OfferRejectedByBuyer notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.ChangeRequestClosed:
            return <ChangeRequestClosed notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.ChangeRequestAccepted:
            return <ChangeRequestAccepted notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.DeadlineReminderAllSent:
            return <DeadlineReminderAllSent notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.DeadlineReminderNoOptions:
            return <DeadlineReminderNoOptions notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.DeadlineReminderWithUnsent:
            return <DeadlineReminderWithUnsent notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.BuyerSignedOptions:
            return <BuyerSignedOptions notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.ChangeRequestRequestedNewOffers:
            return <ChangeRequestRequestedNewOffers notification={notification} handleClick={handleClick} />;
        case NotificationTemplateType.ZippedFileReady:
            return <ZippedFileReady notification={notification} handleClick={handleClick} />;

        default:
            return <NotImplemented notification={notification} handleClick={handleClick} />;
    }
};

const appendInProjectMessage = (projectName: string) => {
    if (projectName) {
        return (
            <span>
                {' '}
                <Trans>
                    in <strong>{projectName}</strong>
                </Trans>
            </span>
        );
    }
};

interface DataObject {
    ResidentialUnitId: number | undefined;
    ProfileId?: number | undefined;
    ProjectId: number | undefined;
    ChangeRequestId: number;
}
const generateChangeRequestUrl = ({ ResidentialUnitId, ProfileId, ProjectId, ChangeRequestId }: DataObject) => {
    if (ResidentialUnitId) {
        if (ProfileId && ProjectId) {
            return `/profiles/${ProfileId}/projects/${ProjectId}/residential-units/${ResidentialUnitId}/changes/${ChangeRequestId}`;
        }
        if (ProjectId) {
            return `/${ProjectId}/residential-units/${ResidentialUnitId}/changes/${ChangeRequestId}`;
        }
        return `/residential-units/${ResidentialUnitId}/changes/${ChangeRequestId}`;
    }
    if (ProfileId && ProjectId) {
        return `/profiles/${ProfileId}/projects/${ProjectId}/changes/${ChangeRequestId}`;
    }
    if (ProjectId) {
        return `/projects/${ProjectId}/changes/${ChangeRequestId}`;
    }
    return `/changes/${ChangeRequestId}`;
};

const ChangeRequestCreated: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: ChangeRequestCreatedData = notification.data;

    return (
        <LinkRow
            url={generateChangeRequestUrl(data)}
            icon={<Icon iconName="Exchange" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>New change request received from the buyer of {data.UnitName}</Trans>
            {appendInProjectMessage(data.ProjectName)}
        </LinkRow>
    );
};

const OfferOnChangeRequestToBuyer: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: OfferOnChangeRequestToBuyerData = notification.data;

    return (
        <LinkRow
            url={`/profiles/${data.ProfileId}/change-requests/${data.ChangeRequestId}/offer`}
            icon={<Icon iconName="Exchange" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>You have received an offer for a request you sent - {data.Name}</Trans>
        </LinkRow>
    );
};

const DeadlineApproaching: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: DeadlineApproachingData = notification.data;

    return (
        <LinkRow
            url={`/profiles/${data.ProfileId}/options`}
            icon={<Icon iconName="BallotCheck" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>{data.Name} date arrives soon. Make sure you send your selections in time</Trans>
        </LinkRow>
    );
};

const TaggedInChat: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: TaggedInChatData | TaggedInChatVer2 = notification.data;
    const { _ } = useLingui();

    if (data.Version === 2) {
        if (data.IsBuyer) {
            return (
                <LinkRow
                    url={`/ext/chat/${data.CommentThreadId}`}
                    icon={<Icon iconName="Comments" />}
                    notification={notification}
                    handleClick={handleClick}>
                    <Trans>You have been tagged in a message by {data.TaggedByName}</Trans>
                </LinkRow>
            );
        }
        return (
            <LinkRow
                url={`/ext/chat/${data.CommentThreadId}`}
                icon={null}
                notification={notification}
                handleClick={handleClick}>
                <div className={theme.pillRow}>
                    <div className={classnames(theme.badge, theme.removePadding)}>
                        {_({ id: `CommentThreadType.${data.ThreadType}` })}
                    </div>
                    {data.IsMainThread && (
                        <div className={theme.badge}>
                            <Trans>Main thread</Trans>
                        </div>
                    )}
                    {data.IncludesBuyer ? (
                        <div className={theme.includesBuyerBadge}>
                            <Trans>Includes buyer</Trans>
                        </div>
                    ) : (
                        <div className={theme.internalBadge}>
                            <Trans>Internal</Trans>
                        </div>
                    )}
                </div>
                <Trans>
                    You have been tagged in a message by <strong>{data.TaggedByName}</strong>
                </Trans>
                {data.ResidentialUnitName && (
                    <>
                        {' '}
                        <Trans>
                            connected to unit <strong>{data.ResidentialUnitName}</strong>
                        </Trans>
                    </>
                )}
                {appendInProjectMessage(data.ProjectName)}
            </LinkRow>
        );
    } else {
        if (data.IsBuyer) {
            return (
                <LinkRow
                    url={`/ext/chat/${data.CommentThreadId}`}
                    icon={<Icon iconName="Comments" />}
                    notification={notification}
                    handleClick={handleClick}>
                    <Trans>You have been tagged in a message</Trans>
                </LinkRow>
            );
        }
        return (
            <LinkRow
                url={`/ext/chat/${data.CommentThreadId}`}
                icon={<Icon iconName="Comments" />}
                notification={notification}
                handleClick={handleClick}>
                <Trans>You have been tagged in a message</Trans>
                {appendInProjectMessage(data.ProjectName)}
            </LinkRow>
        );
    }
};

const InvitedUserAccepted: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: InvitedUserAcceptedData = notification.data;

    return (
        <LinkRow
            url={`/profiles/${data.InviterProfileId}/projects/${data.ProjectId}/residential-units/${data.ResidentialUnitId}`}
            icon={<Icon iconName="User" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>
                {data.Email} has accepted your invitation to {data.Unit}
            </Trans>
            {appendInProjectMessage(data.ProjectName)}
        </LinkRow>
    );
};

const ProjectInviteAccepted: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: ProjectInviteAcceptedData = notification.data;

    return (
        <LinkRow
            url={`/profiles/${data.InviterProfileId}/projects/${data.ProjectId}/users`}
            icon={<Icon iconName="User" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>
                {data.Email} accepted your invitation to participate in <strong>{data.ProjectName}</strong>
            </Trans>
        </LinkRow>
    );
};

const NewLandingPageMessage: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: NewLandingPageMessageData = notification.data;

    return (
        <LinkRow
            url="/landing/news"
            icon={<ProfileImage profileId={notification.initiatedByProfileId!} />}
            notification={notification}
            handleClick={handleClick}>
            {data.Header}
        </LinkRow>
    );
};

const TaskChange: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: TaskChangeData = notification.data;

    const getUrl = () => {
        if (data.Type === TaskType.DocumentLink && data.Approved) {
            return `/projects/${data.ProjectId}/residential-units/${data.ResidentialUnitId}/documents/${data.DocumentId}`;
        } else if (data.Type === TaskType.DocumentLink && data.Rejected) {
            return `/projects/${data.ProjectId}/tasks/${
                data.IsProjectDocument ? 'project-documents' : 'documents'
            }/rejected/${data.DocumentId}`;
        } else if (data.Type === TaskType.OptionGroup && data.Approved) {
            return `/projects/${data.ProjectId}/linked-options/${data.BuyerOptionGroupBatchId}`;
        } else if (data.Type === TaskType.OptionGroup && data.Rejected) {
            return `/projects/${data.ProjectId}/tasks/buyer-options/rejected/${data.BuyerOptionGroupBatchId}`;
        }

        return '';
    };

    const getText = () => {
        if (data.Approved && data.Count) {
            return (
                <Trans>
                    {data.Name} has been published to {data.Count} units
                </Trans>
            );
        } else if (data.Approved && data.UnitName) {
            return (
                <Trans>
                    {data.Name} has been published to {data.UnitName}
                </Trans>
            );
        } else if (data.Rejected && data.Count) {
            return (
                <Trans>
                    {data.Name} has been rejected to {data.Count} units
                </Trans>
            );
        } else if (data.Rejected && data.UnitName) {
            return (
                <Trans>
                    {data.Name} has been rejected to {data.UnitName}
                </Trans>
            );
        } else {
            return <Trans>Oops, something went wrong</Trans>;
        }
    };

    const getIconName = () => {
        if (data.Type === TaskType.OptionGroup) {
            return 'DollyFlatbed';
        } else if (data.IsProjectDocument) {
            return 'Folders';
        }

        return 'Folder';
    };

    return (
        <LinkRow
            url={getUrl()}
            icon={<Icon iconName={getIconName()} />}
            notification={notification}
            handleClick={handleClick}>
            {getText()}
            {appendInProjectMessage(data.ProjectName)}
        </LinkRow>
    );
};

const ChangeRequestAddedAttachment: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: ChangeRequestAddedAttachmentData = notification.data;

    return (
        <LinkRow
            url={generateChangeRequestUrl(data)}
            icon={<Icon iconName="Exchange" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>
                {data.UnitName} has added a attachment to change request: "{data.ChangeRequestName}"
            </Trans>
            {appendInProjectMessage(data.ProjectName)}
        </LinkRow>
    );
};

const DocumentAdded: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: DocumentAddedData = notification.data;

    return (
        <LinkRow
            url={`/profiles/${data.ProfileId}/files/${data.VersionId ? data.VersionId : ''}`}
            icon={<Icon iconName="Folder" />}
            notification={notification}
            handleClick={handleClick}>
            <>
                <Trans>
                    <strong> {data.DocumentName}</strong> ({data.DocumentTypeName}) has been added to your documents.
                </Trans>
                <br />
            </>
        </LinkRow>
    );
};

const ChangeRequestAddedParticipant: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: ChangeRequestAddedParticipantData = notification.data;

    return (
        <LinkRow
            url={generateChangeRequestUrl(data)}
            icon={<Icon iconName="Exchange" />}
            notification={notification}
            handleClick={handleClick}>
            <>
                <Trans>
                    You have been added as a participant of a change request "{data.ChangeRequestName}" connected to the unit{' '}
                    <strong>{data.UnitName}</strong>
                </Trans>
                {appendInProjectMessage(data.ProjectName)}
            </>
        </LinkRow>
    );
};

const OfferRejectedByBuyer: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: OfferRejectedByBuyerData = notification.data;

    return (
        <LinkRow
            url={generateChangeRequestUrl(data)}
            icon={<Icon iconName="Exchange" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>
                The buyer of {data.UnitName} has rejected the offer for "{data.ChangeRequestName}"
            </Trans>
            {appendInProjectMessage(data.ProjectName)}
        </LinkRow>
    );
};

const ChangeRequestClosed: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: ChangeRequestClosedData = notification.data;

    return (
        <LinkRow
            url={generateChangeRequestUrl(data)}
            icon={<Icon iconName="Exchange" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>A change request has been closed - {data.ChangeRequestName}</Trans>
            bug/sc-11325/change-cr-inappnotifications-to-link-to-the
        </LinkRow>
    );
};

const ChangeRequestAccepted: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: ChangeRequestAcceptedData = notification.data;

    return (
        <LinkRow
            url={generateChangeRequestUrl(data)}
            icon={<Icon iconName="Exchange" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>
                The buyer of {data.UnitName} has accepted the offer for "{data.ChangeRequestName}"
            </Trans>
            {appendInProjectMessage(data.ProjectName)}
        </LinkRow>
    );
};

const DeadlineReminderAllSent: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: DeadlineReminderAllSentData = notification.data;
    const url = data.ProfileId ? `/profiles/${data.ProfileId}/options` : `/options`;

    return (
        <LinkRow url={url} icon={<Icon iconName="Exchange" />} notification={notification} handleClick={handleClick}>
            <Trans>
                Deadline {data.Name} is in {data.NumberOfDaysToDeadline} days. Your choices for this deadline have been
                received.
            </Trans>
        </LinkRow>
    );
};

const DeadlineReminderNoOptions: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: DeadlineReminderNoOptionsData = notification.data;

    return (
        <NoLinkRow icon={<Icon iconName="Exchange" />} notification={notification} handleClick={handleClick}>
            <Trans>
                Deadline {data.Name} is in {data.NumberOfDaysToDeadline} days.
            </Trans>
        </NoLinkRow>
    );
};

const DeadlineReminderWithUnsent: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: DeadlineReminderWithUnsentData = notification.data;
    const url = data.ProfileId ? `/profiles/${data.ProfileId}/options` : `/options`;

    return (
        <LinkRow url={url} icon={<Icon iconName="Exchange" />} notification={notification} handleClick={handleClick}>
            <Trans>
                Deadline {data.Name} is in {data.NumberOfDaysToDeadline} days. Remember to send your choices before that.
            </Trans>
        </LinkRow>
    );
};

const BuyerSignedOptions: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: BuyerSignedOptionsData = notification.data;

    return (
        <LinkRow
            url={`/profiles/${data.ProfileId}/projects/${data.ProjectId}/residential-units/${data.ResidentialUnitId}/options`}
            icon={<Icon iconName="DollyFlatbed" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>
                {data.UnitName} has sent the options for the deadline "{data.DeadlineName}"
            </Trans>
            {appendInProjectMessage(data.ProjectName)}
        </LinkRow>
    );
};

const ChangeRequestRequestedNewOffers: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: SendChangeRequestRequestedNewOffersData = notification.data;

    return (
        <LinkRow
            url={generateChangeRequestUrl(data)}
            icon={<Icon iconName="Exchange" />}
            notification={notification}
            handleClick={handleClick}>
            <Trans>Quotes required from you for "{data.ChangeRequestSubject}"</Trans>
            {appendInProjectMessage(data.ProjectName)}
        </LinkRow>
    );
};

const ZippedFileReady: React.FC<DataProps> = ({ notification, handleClick }) => {
    const data: SendZippedFileReadyData = notification.data;

    return (
        <ExternalLinkRow
            url={getZipDownloadUrl(data.Id)}
            icon={
                <div className={theme.image}>
                    <FontAwesomeIcon icon={faFileArchive} />
                </div>
            }
            notification={notification}
            handleClick={handleClick}>
            <Trans>Your zipped file of {data.NumberOfFiles} documents is ready to download.</Trans>
        </ExternalLinkRow>
    );
};

const NotImplemented: React.FC<DataProps> = ({ notification, handleClick }) => {
    return (
        <NoLinkRow icon={<IconPlaceHolder />} notification={notification} handleClick={handleClick}>
            <Trans>Oops, something went wrong</Trans>
        </NoLinkRow>
    );
};

const NoLinkRow: React.FC<NoLinkRowProps> = ({ notification, handleClick, icon, children }) => {
    return (
        <div
            onClick={() => {
                handleClick();
            }}
            className={classnames(theme.notificationRow, theme[notification.status.toLowerCase()])}>
            {icon}
            <div>
                <span className={theme.message}>{children}</span>
                <span className={theme.date}>
                    <DateDistanceDisplay value={notification.createDate} />
                </span>
            </div>
        </div>
    );
};

const ExternalLinkRow: React.FC<LinkRowProps> = ({ notification, handleClick, icon, children, url }) => {
    return (
        <ExternalLink
            onClick={() => {
                handleClick();
            }}
            className={classnames(theme.notificationRow, theme[notification.status.toLowerCase()])}
            href={url}>
            {icon}
            <div>
                <span className={theme.message}>{children}</span>
                <span className={theme.date}>
                    <DateDistanceDisplay value={notification.createDate} />
                </span>
            </div>
        </ExternalLink>
    );
};

const LinkRow: React.FC<LinkRowProps> = ({ notification, handleClick, icon, children, url }) => {
    return (
        <CustomLink
            onClick={() => {
                handleClick();
            }}
            className={classnames(theme.notificationRow, theme[notification.status.toLowerCase()])}
            to={url}>
            {icon}
            <div>
                <span className={theme.message}>{children}</span>
                <span className={theme.date}>
                    <DateDistanceDisplay value={notification.createDate} />
                </span>
            </div>
        </CustomLink>
    );
};

const ProfileImage: React.FC<{ profileId: number }> = ({ profileId }) => {
    return (
        <div className={classnames(theme.image, theme.profileIcon)}>
            <ProfileIcon sizeInPx={44} profileId={profileId!} />
        </div>
    );
};

const Icon: React.FC<{ iconName: BackendIconNames }> = ({ iconName }) => {
    return (
        <div className={theme.image}>
            <BackendIcon iconName={iconName} />
        </div>
    );
};

// const Image: React.FC<{ url: string }> = ({ url }) => {
//     return (
//         <div className={theme.image}>
//             <Thumbnail src={url} />
//         </div>
//     );
// };

const IconPlaceHolder: React.FC<{}> = () => {
    return <div className={theme.image}></div>;
};

export default NotificationRow;
